import {Tag, Text, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {hexToRgb} from "@mui/material";
import {
  CSSProperties,
  MouseEvent,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import {DriverQualification, Manifest, ManifestStop, Skill} from "../../../../../../../generated/graphql";
import {PreferenceContext} from "../../../../../../../providers/PreferenceProvider";
import {hasQueuedStops, isCompleted, isOnSite} from "../../../../../../../utils/stops/StopUtils";
import {getMaxStopCount, hasTooManyStops} from "../../../../../../../utils/ManifestUtils";
import {getStopAddressText, isSmallCard} from "../../../../../../../utils/General";
import {EmptyValueStrings} from "../../../../../../../constants/constants";
import {
  ColorRank,
  DispatchRulesDataContext,
  DispatchRulesDataState
} from "../../../../../../../providers/DispatchRulesDataProvider";
import {ServiceBadge} from "../../../../../../common/ServiceBadge";
import {getDriverCode, getDriverName} from "../../../../../../map/driver/DriverTitle";
import {LocationMarker} from "../../../../../../map/LocationMarker";
import {ManifestVisibilityState, useMapVisibilityState} from "../../../../../../map/MapVisibilityContext";
import {IndicatorColors} from "../../../../../../settings/ColorSettings/ColorizedIndicators/ColorizedIndicators";
import {UserColor} from "../../../../../../settings/ColorSettings/ColorizedIndicators/types/indicatorLogicType";
import vehicleIconResolver from "../../../../../../vehicles/VehicleIconResolver";
import {IManifestCardConfigurationsV2} from "../../../../../card-configuration/v2/types/manifest-card-configurations.type";
import {
  CardTypeV2,
  DriverInformationValue,
  HoverOnStopsValues,
  StopInformationValue,
  TopStopInformationValue
} from "../../../../../card-configuration/v2/constants/enums";
import {HOVER_ON_STOPS_LABELS} from "../../../../../card-configuration/v2/constants/constants";
import DriverQualV3 from "./DriverQual";
import DriverToolTipContent from "./DriverToolTip";
import {StopStatusIconV3} from "../icons/StatusIconV3";
import {renderStopTypeIcon} from "../../../../../../common/icons/StopTypeIcons";
import StopCountIndicator from "../../../StopCountIndicator";
import {getTimeStampValueByType, StopDateTimeType} from "../../../../../../common/cell-renderers/DateTimeCellRenderer";
import {css} from "@emotion/react";
import {useDispatchDataContext} from "../../../../../../../providers/DispatchDataProvider";
import {TManifestStopEnhanced} from "../../../../../../common/types";

//Types
type ManifestCardDriverRowProps = {
  manifest: Manifest;
  handleDrillDownManifest(e: MouseEvent, openedManifest: Manifest): void;
  onLocationMarkerClick(manifest: Manifest): void;
  stopsColor: StopColor[];
  isSelected: boolean;
  disabled?: boolean;
  cardConfigurations: IManifestCardConfigurationsV2;
};

type StopInfoItemProps = {
  index?: number;
  stop: TManifestStopEnhanced;
  color: string;
  disabled: boolean;
  cardConfigurations: IManifestCardConfigurationsV2;
  isTimeFormat24hr: boolean;
  timezoneOfJobStop: boolean;
};

export type StopColor = {
  color: string;
  jobStopID?: number;
};

type TDriverInfo = {
  configurationState: IManifestCardConfigurationsV2;
  manifest: Manifest;
  disabled: boolean;
  handleDrillDownManifest: (e: MouseEvent, openedManifest: Manifest) => void;
  skills?: Skill[];
};

//Utils
export const getHighestRankedColor = (colors: StopColor[], colorRank: ColorRank[]) => {
  const sortedColors = colors
    .filter((color) => color.color)
    .sort((a, b) => {
      const colorA = colorRank.find((rank: ColorRank) => rank.color === a.color);
      const colorB = colorRank.find((rank: ColorRank) => rank.color === b.color);

      return colorA && colorB ? colorA.rank - colorB.rank : 0;
    });

  return sortedColors[0]?.color;
};

const renderDriverLeftInfo = ({configurationState, manifest, handleDrillDownManifest, disabled}: TDriverInfo) => {
  const [driverCode, driverName, driverCapabilities, driverSkills, driverCodeAndName] = [
    DriverInformationValue.DriverCode,
    DriverInformationValue.DriverName,
    DriverInformationValue.DriverCapabilities,
    DriverInformationValue.DriverSkills,
    DriverInformationValue.DriverCodeAndName
  ].map((item) => configurationState.driverInformation?.includes(item));

  if (driverCodeAndName || (driverName && driverCode && (driverCapabilities || driverSkills))) {
    const text =
      getDriverCode(manifest.driver) + (getDriverCode(manifest.driver) ? " - " : "") + getDriverName(manifest.driver);
    return (
      <Text title="" ellipsize data-testid={"driver-left-info"} onClick={(e) => handleDrillDownManifest(e, manifest)}>
        {disabled ? text : <a href="/.">{text}</a>}
      </Text>
    );
  }

  if (driverCode && (driverCapabilities || driverSkills) && !driverName) {
    return (
      <Text title="" ellipsize data-testid={"driver-left-info"} onClick={(e) => handleDrillDownManifest(e, manifest)}>
        {disabled ? getDriverCode(manifest.driver) : <a href="/.">{getDriverCode(manifest.driver)}</a>}
      </Text>
    );
  }

  if (driverName) {
    return (
      <Text
        title=""
        ellipsize
        className={disabled ? "bp5-disabled" : ""}
        data-testid={"driver-left-info"}
        onClick={(e) => handleDrillDownManifest(e, manifest)}
      >
        {disabled ? getDriverName(manifest.driver) : <a href="/.">{getDriverName(manifest.driver)}</a>}
      </Text>
    );
  }

  return "";
};

const renderDriverRightInfo = ({
  configurationState,
  manifest,
  handleDrillDownManifest,
  disabled,
  skills
}: TDriverInfo) => {
  const {cardType, driverInformation} = configurationState;
  const [driverCode, driverCapabilities, driverSkills] = [
    DriverInformationValue.DriverCode,
    DriverInformationValue.DriverCapabilities,
    DriverInformationValue.DriverSkills
  ].map((item) => driverInformation?.includes(item));

  if ((driverCapabilities || driverSkills) && cardType !== CardTypeV2.Micro) {
    const activeSkillIds = (manifest.driver.skills ?? []).map((el) => el.id);
    const activeDriverSkills = (skills ?? [])
      .filter((el) => activeSkillIds.includes(el.id))
      .map((el) => ({
        id: el.id,
        code: el.name.slice(0, 3).toUpperCase(),
        name: el.name
      })) as unknown as DriverQualification[];

    const driverQualifications = driverCapabilities ? manifest.driver.qualifications : activeDriverSkills;
    return (
      <>
        <DriverQualV3 qualifications={driverQualifications ?? []} />
        {(driverQualifications ?? []).length > 0 && <Divider height={12} />}
      </>
    );
  }

  if (driverCode) {
    return (
      <>
        <Tooltip
          placement="bottom"
          popoverClassName="manifest-card-header-popover"
          content={getDriverCode(manifest.driver)}
        >
          <Text
            className={disabled ? "bp5-disabled" : ""}
            ellipsize
            style={{maxWidth: cardType === CardTypeV2.Micro ? "50px" : "80px"}}
            title=""
            data-testid={"driver-right-info"}
            onClick={(e) => handleDrillDownManifest(e, manifest)}
          >
            {disabled ? getDriverCode(manifest.driver) : <a href="/.">{getDriverCode(manifest.driver)}</a>}
          </Text>
        </Tooltip>
        {getDriverCode(manifest.driver) && cardType !== CardTypeV2.Micro && <Divider height={12} />}
      </>
    );
  }

  return "";
};

// Components
export const ManifestCardHeaderV2 = ({
  manifest,
  handleDrillDownManifest,
  onLocationMarkerClick,
  stopsColor,
  isSelected,
  cardConfigurations,
  disabled = false
}: ManifestCardDriverRowProps) => {
  const {tenantPreferences} = useContext(PreferenceContext);
  const mapVisibilityState = useMapVisibilityState();
  const {skills} = useDispatchDataContext();
  const manifestVisibility =
    mapVisibilityState.manifests.get(manifest.manifestDriverId) ??
    new ManifestVisibilityState(manifest.manifestDriverId, manifest.driver.driverId);
  const {cardType, driverInformation} = cardConfigurations;

  const [indicatorColor, setIndicatorColor] = useState("");
  const ruleContextState = useContext<DispatchRulesDataState>(DispatchRulesDataContext);

  const tooManyStops = hasTooManyStops(manifest);
  const maxStopCount = getMaxStopCount(manifest);
  const queuedStops = hasQueuedStops(manifest.stops);

  const showStatusIcon = useMemo(() => {
    if (isSmallCard(cardType)) {
      return tooManyStops || driverInformation.includes(DriverInformationValue.QueuedStopHourglass);
    }
    return true;
  }, [cardType, tooManyStops, driverInformation]);

  const driverHoverConfigs = useMemo(
    () => cardConfigurations.hoverDriverConfig.map((item) => item.value),
    [cardConfigurations.hoverDriverConfig]
  );

  useEffect(() => {
    if (!ruleContextState.loading && ruleContextState.colorRank) {
      setIndicatorColor(getHighestRankedColor(stopsColor, ruleContextState.colorRank));
    }
  }, [ruleContextState, stopsColor, manifest]);

  return (
    <HeaderContainer disabled={disabled}>
      <LeftSideHeader>
        <StopCountIndicator stops={manifest?.stops} indicatorColor={indicatorColor} cardType={cardType} />
        <Tooltip
          placement="bottom"
          popoverClassName="manifest-card-header-popover"
          content={<DriverToolTipContent manifest={manifest} driverHoverConfigs={driverHoverConfigs} />}
          disabled={driverHoverConfigs?.length === 0}
        >
          {renderDriverLeftInfo({manifest, configurationState: cardConfigurations, handleDrillDownManifest, disabled})}
        </Tooltip>
      </LeftSideHeader>
      <RightSideHeader>
        {renderDriverRightInfo({
          manifest,
          configurationState: cardConfigurations,
          handleDrillDownManifest,
          disabled,
          skills
        })}
        {showStatusIcon && (
          <>
            <StopStatusIconV3
              tooManyStops={tooManyStops}
              maxStopCount={maxStopCount}
              queuedStops={queuedStops}
              disabled={disabled}
            />
            <Divider height={12} />
          </>
        )}
        <FontAwesomeIcon
          data-testid={"vehicle-icon"}
          size={cardType === CardTypeV2.Micro ? "sm" : "lg"}
          color={isSelected ? "#436BF8" : "#4C505E"}
          icon={vehicleIconResolver(manifest.vehicleType.description, tenantPreferences)}
          title={manifest.vehicleType.description as string}
        />
        <Divider height={12} />
        <LocationMarkerContainer
          data-testid={`map-icon-manifest-${manifest.manifestDriverId}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) {
              onLocationMarkerClick(manifest);
            }
          }}
        >
          <LocationMarker
            isVisible={manifestVisibility.isVisible()}
            color={disabled ? "rgba(95, 107, 124, 0.6)" : manifestVisibility.routeColor}
            error={manifestVisibility.error}
          />
        </LocationMarkerContainer>
      </RightSideHeader>
    </HeaderContainer>
  );
};

const StopTime = ({
  children,
  color,
  textColor,
  isTimeFormat24hr
}: {
  children: any;
  color: string;
  textColor: string;
  isTimeFormat24hr: boolean;
}) => {
  const styles: CSSProperties = {
    position: "relative",
    backgroundColor: color ? `rgba(${hexToRgb(color).substring(4, hexToRgb(color).length - 1)}, 0.3)` : "",
    color: color || textColor,
    width: "100%",
    height: "20px",
    borderRadius: "4px",
    fontSize: "11px",
    lineHeight: "20px",
    fontWeight: 500,
    padding: "0 4px",
    letterSpacing: isTimeFormat24hr ? "0.5px" : "-0.4px",
    textAlign: "center"
  };
  return (
    <div className="stop-time" style={styles}>
      {children}
    </div>
  );
};

export const StopAddressHoverContent = ({
  stop,
  stopHoverConfigs,
  additionalContents,
  showOnSiteTag
}: {
  stop: ManifestStop;
  stopHoverConfigs: string[] | undefined;
  additionalContents?: string[];
  showOnSiteTag?: boolean;
}) => {
  const customer = stop.order.customer.name;
  const service = stop.order.service;
  const stopName = stop.name;
  const stopAddress = getStopAddressText({stop});
  const stopPhone = stop.phone;
  const stopSee = stop.see;
  const stopNote = stop.note;
  const stopRoom = stop.room;
  const stopDescription = stop.order.description;
  const jobRouteNumber = stop.job.routeNumber;
  const orderAlias = stop.order.alias;
  const jobAlias = stop.job.alias;
  const orderAuth = stop.order.auth;
  const orderOtherReference = stop.order.otherReference;
  const orderInvoiceReference = stop.order.invoiceReference;
  const jobNumber = stop.job.jobNumber;
  //Function
  const renderStopPieceOrWeight = useCallback(() => {
    return `${stop.job.pieces ?? EmptyValueStrings.notProvided} / ${stop.job.weight ?? EmptyValueStrings.notProvided}`;
  }, [stop.job.pieces, stop.job.weight]);

  const renderCallerAndPhone = useCallback(() => {
    return `${stop.order.caller ?? EmptyValueStrings.notProvided} & ${
      stop.order.callerPhone ?? EmptyValueStrings.notProvided
    }`;
  }, [stop.order.caller, stop.order.callerPhone]);

  const renderCODStatusAndAmount = useCallback(() => {
    return `${stop.order.codStatus ?? EmptyValueStrings.notProvided} & ${
      stop.order.codAmount ?? EmptyValueStrings.notProvided
    }`;
  }, [stop.order.codAmount, stop.order.codStatus]);

  const renderHoverContent = useMemo(() => {
    const renderHover = new Map<string, {content: string | number}>();
    const mapCardConfigs = (key: string, content: string | number | undefined | null) => {
      // DP-3156: the tooltip prints this Map key as the row label, so it has to be the display
      // label rather than the enum value (which stays "Alias" as the persisted identifier).
      renderHover.set(HOVER_ON_STOPS_LABELS[key as HoverOnStopsValues] ?? key, {
        content: content ?? EmptyValueStrings.notProvided
      });
    };
    stopHoverConfigs?.forEach((config) => {
      switch (config) {
        case HoverOnStopsValues.CUSTOMER:
          mapCardConfigs(HoverOnStopsValues.CUSTOMER, customer);
          break;
        case HoverOnStopsValues.STOP_ADDRESS:
          mapCardConfigs(HoverOnStopsValues.STOP_ADDRESS, stopAddress);
          break;
        case HoverOnStopsValues.STOP_NAME:
          mapCardConfigs(HoverOnStopsValues.STOP_NAME, stopName);
          break;
        case HoverOnStopsValues.STOP_PHONE:
          mapCardConfigs(HoverOnStopsValues.STOP_PHONE, stopPhone);
          break;
        case HoverOnStopsValues.STOP_SEE:
          mapCardConfigs(HoverOnStopsValues.STOP_SEE, stopSee);
          break;
        case HoverOnStopsValues.STOP_NOTES:
          mapCardConfigs(HoverOnStopsValues.STOP_NOTES, stopNote);
          break;
        case HoverOnStopsValues.SERVICE:
          mapCardConfigs(HoverOnStopsValues.SERVICE, service);
          break;
        case HoverOnStopsValues.DESCRIPTION:
          mapCardConfigs(HoverOnStopsValues.DESCRIPTION, stopDescription);
          break;
        case HoverOnStopsValues.PIECES_OR_WEIGHT:
          mapCardConfigs(HoverOnStopsValues.PIECES_OR_WEIGHT, renderStopPieceOrWeight());
          break;
        case HoverOnStopsValues.ROUTE_NUMBER:
          mapCardConfigs(HoverOnStopsValues.ROUTE_NUMBER, jobRouteNumber);
          break;
        case HoverOnStopsValues.ALIAS:
          mapCardConfigs(HoverOnStopsValues.ALIAS, orderAlias);
          break;
        case HoverOnStopsValues.JOB_ALIAS:
          mapCardConfigs(HoverOnStopsValues.JOB_ALIAS, jobAlias);
          break;
        case HoverOnStopsValues.STOP_ROOM:
          mapCardConfigs(HoverOnStopsValues.STOP_ROOM, stopRoom);
          break;
        case HoverOnStopsValues.CALLER_AND_PHONE:
          mapCardConfigs(HoverOnStopsValues.CALLER_AND_PHONE, renderCallerAndPhone());
          break;
        case HoverOnStopsValues.AUTH:
          mapCardConfigs(HoverOnStopsValues.AUTH, orderAuth);
          break;
        case HoverOnStopsValues.COD_STATUS_AND_AMOUNT:
          mapCardConfigs(HoverOnStopsValues.COD_STATUS_AND_AMOUNT, renderCODStatusAndAmount());
          break;
        case HoverOnStopsValues.OTHER_REFERENCE:
          mapCardConfigs(HoverOnStopsValues.OTHER_REFERENCE, orderOtherReference);
          break;
        case HoverOnStopsValues.INVOICE_REFERENCE:
          mapCardConfigs(HoverOnStopsValues.INVOICE_REFERENCE, orderInvoiceReference);
          break;
        case HoverOnStopsValues.JOB_NUMBER:
          mapCardConfigs(HoverOnStopsValues.JOB_NUMBER, jobNumber);
          break;
      }
    });
    return renderHover;
  }, [
    customer,
    stopHoverConfigs,
    jobNumber,
    jobRouteNumber,
    orderAlias,
    jobAlias,
    orderAuth,
    orderInvoiceReference,
    orderOtherReference,
    renderCODStatusAndAmount,
    renderCallerAndPhone,
    renderStopPieceOrWeight,
    service,
    stopAddress,
    stopDescription,
    stopName,
    stopNote,
    stopPhone,
    stopRoom,
    stopSee
  ]);

  const renderHoverElement = useCallback(() => {
    const arrayElement = Array.from(renderHoverContent);
    return arrayElement.map(([key, value]) => (
      <Text key={key}>
        {key}: {value.content}
      </Text>
    ));
  }, [renderHoverContent]);

  return (
    <StopInfoHoverContainer data-testid="stop-info-hover-tooltip-container">
      {showOnSiteTag && (
        <CustomTag minimal intent="success">
          ON SITE
        </CustomTag>
      )}
      {additionalContents?.map((c) => (
        <Text className="primary" key={c}>
          {c}
        </Text>
      ))}
      {stopHoverConfigs ? (
        renderHoverElement()
      ) : (
        <>
          <Text className="primary">{stopAddress}</Text>
          <Text>Customer: {stop.order.customer.name}</Text>
          {stop.see && <Text>See: {stop.see}</Text>}
          <Text>Phone: {stop.phone}</Text>
          <Text>Route #: {stop.job.routeNumber}</Text>
        </>
      )}
    </StopInfoHoverContainer>
  );
};

const StopConfiguredInfo = ({
  stop,
  textColor,
  isTimeFormat24hr = false,
  timezoneOfJobStop = false,
  showFirstStopLabel = false,
  cardConfigurations
}: {
  stop: TManifestStopEnhanced;
  textColor: string;
  cardConfigurations: IManifestCardConfigurationsV2;
  showFirstStopLabel?: boolean;
  isTimeFormat24hr?: boolean;
  timezoneOfJobStop?: boolean;
}) => {
  const [additionalContents, setAdditionalContents] = useState<string[]>([]);
  const {cardType, hoverStopsConfig, stopInformation} = cardConfigurations;

  const stopInfoFields = useMemo(
    () => cardConfigurations.stopInformation?.map((item) => item.field),
    [cardConfigurations.stopInformation]
  );
  const stopInfoWidths = useMemo(
    () => cardConfigurations.stopInformation?.map((item) => item.width),
    [cardConfigurations.stopInformation]
  );

  const stopInfoContent = useMemo(() => {
    return stopInfoFields?.map((field, index) => {
      let ContainerEl = index === 0 ? PrimaryText : SecondaryText;
      let fieldContent;
      let ignoreTooltip = false;
      switch (field) {
        case StopInformationValue.CustomerName:
          fieldContent = stop.order.customer.name ?? "";
          break;

        case StopInformationValue.Address:
        case StopInformationValue.AddressAndZone:
        case StopInformationValue.CityStateZone:
        case StopInformationValue.Zone:
          fieldContent = getStopAddressText({stop, stopLocationState: field});
          break;
        case StopInformationValue.ETATime: {
          let conditionalETAValue;
          if (isCompleted(stop)) {
            conditionalETAValue = "Done";
          } else if (isOnSite(stop)) {
            conditionalETAValue = (
              <CustomTag minimal intent="success">
                ON SITE
              </CustomTag>
            );
          } else {
            conditionalETAValue = getTimeStampValueByType(
              stop,
              timezoneOfJobStop,
              isSmallCard(cardType) ? true : isTimeFormat24hr,
              StopDateTimeType.ETA,
              {amPmFormat: "aaa", includeZone: false}
            );
          }
          fieldContent = conditionalETAValue;
          break;
        }
        case StopInformationValue.PhoneNumber:
          fieldContent = stop.phone ?? "";
          break;
        case StopInformationValue.StopName:
          fieldContent = stop.name ?? "";
          break;
        case StopInformationValue.Alias:
          fieldContent = stop.order.alias ?? "";
          break;
        case StopInformationValue.TotalPieces:
          fieldContent = stop.job.pieces ? stop.job.pieces + " pcs" : "";
          break;
        case StopInformationValue.InvoiceReference:
          fieldContent = stop.order.invoiceReference ?? "";
          break;
        case StopInformationValue.ServiceType:
          ContainerEl = styled.div``;
          fieldContent = <StopService stop={stop} showFirstStopLabel={showFirstStopLabel} />;
          ignoreTooltip = true;
          break;
        case StopInformationValue.JobNumber:
          fieldContent = stop.job.jobNumber ?? "";
          break;
        case StopInformationValue.Auth:
          fieldContent = stop.order.auth ?? "";
          break;
        case StopInformationValue.RouteNumber:
          fieldContent = stop.job.routeNumber ? "R# " + stop.job.routeNumber : "";
          break;
        default:
      }
      return {
        key: field as StopInformationValue,
        content: (
          <ContainerEl data-testid="stop-item" className="content" key={field}>
            {fieldContent}
          </ContainerEl>
        ),
        ignoredTooltip: ignoreTooltip
      };
    });
  }, [cardType, isTimeFormat24hr, showFirstStopLabel, stop, stopInfoFields, timezoneOfJobStop]);

  const stopHoverConfigs = useMemo(() => {
    return hoverStopsConfig.map((item) => item.value);
  }, [hoverStopsConfig]);

  const checkEllipsisActive: MouseEventHandler<HTMLElement> = (e: MouseEvent) => {
    const additionalContents: string[] = [];
    const contentElements = (e.currentTarget as HTMLElement).querySelectorAll(".content") as NodeListOf<HTMLElement>;
    contentElements.forEach((node) => {
      if (node.offsetWidth < node.scrollWidth && node.textContent) additionalContents.push(node.textContent.trim());
    });
    setAdditionalContents(additionalContents);
  };

  const isShowOnSiteBorder = useMemo(() => {
    return !stopInformation?.some((item) => ["etaTime", "serviceType"].includes(item.field)) && isOnSite(stop);
  }, [stopInformation, stop]);

  return (
    <StopConfiguredInfoContainer textColor={textColor} showOnSiteBorder={isShowOnSiteBorder}>
      {stopInfoContent && stopInfoContent.every((item) => !item.ignoredTooltip) ? (
        <Tooltip
          matchTargetWidth
          popoverClassName="manifest-card-address-popover"
          content={
            <StopAddressHoverContent
              stop={stop}
              stopHoverConfigs={stopHoverConfigs}
              additionalContents={additionalContents}
              showOnSiteTag={isShowOnSiteBorder}
            />
          }
          position="bottom"
          disabled={stopHoverConfigs.length === 0}
        >
          <DistributedGrid onMouseEnter={checkEllipsisActive} spaceDistribution={stopInfoWidths}>
            {stopInfoContent.map((item) => item.content)}
          </DistributedGrid>
        </Tooltip>
      ) : (
        <DistributedGrid onMouseEnter={checkEllipsisActive} spaceDistribution={stopInfoWidths}>
          {stopInfoContent &&
            stopInfoContent.map((item) =>
              item.ignoredTooltip ? (
                item.content
              ) : (
                <Tooltip
                  key={item.key}
                  matchTargetWidth
                  popoverClassName="manifest-card-address-popover"
                  content={
                    <StopAddressHoverContent
                      stop={stop}
                      stopHoverConfigs={stopHoverConfigs}
                      additionalContents={additionalContents}
                      showOnSiteTag={isShowOnSiteBorder}
                    />
                  }
                  position="bottom"
                  disabled={stopHoverConfigs.length === 0}
                >
                  {item.content}
                </Tooltip>
              )
            )}
        </DistributedGrid>
      )}
    </StopConfiguredInfoContainer>
  );
};

const StopService = ({stop, showFirstStopLabel}: {stop: ManifestStop; showFirstStopLabel: boolean}) => {
  if (showFirstStopLabel) {
    return (
      <CustomTag
        style={{
          fontSize: "12px",
          padding: "0 3px"
        }}
        minimal
        intent="success"
      >
        Stop 1
      </CustomTag>
    );
  }
  if (isOnSite(stop)) {
    return (
      <CustomTag minimal intent="success">
        ON SITE
      </CustomTag>
    );
  }
  if (isCompleted(stop)) {
    return (
      <Tooltip popoverClassName="manifest-card-service-popover" position="bottom" content="Completed">
        <IconContainer>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12.5 5L6.49042 11.0096L3.50522 8.02439"
              stroke="#A9A9A9"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconContainer>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      popoverClassName="manifest-card-service-popover"
      content={stop.order.service as string}
      position="bottom"
      targetProps={{style: {maxWidth: "100%"}}}
    >
      <ServiceBadge
        service={stop.order.service as string}
        ellipsize
        textStyle={{fontSize: "11px"}}
        tagStyle={{minHeight: "16px", padding: "0 2px"}}
        hideTitle
      />
    </Tooltip>
  );
};

const getStopItemStyles = ({
  stop,
  configurationState,
  color,
  disabled
}: {
  stop: ManifestStop;
  configurationState: IManifestCardConfigurationsV2;
  color: string;
  disabled: boolean;
}) => {
  const isCompletedStop = isCompleted(stop);
  const isFirstStopEnabled = configurationState.topStopInformation === TopStopInformationValue.FirstStopInManifest;
  const isOnSiteStop = isOnSite(stop);

  return isFirstStopEnabled
    ? {
        time: isCompletedStop || disabled ? "#A9A9A9" : color ? "#FFFFFF" : "#000000",
        address: isCompletedStop || disabled ? "#A9A9A9" : "#000000",
        stopType: isCompletedStop || disabled ? "#A9A9A9" : ""
      }
    : {
        time: isCompletedStop || disabled ? "#A9A9A9" : color ? "#FFFFFF" : "#000000",
        address: isOnSiteStop || isCompletedStop || disabled ? "#A9A9A9" : "#000000",
        stopType: isOnSiteStop || isCompletedStop || disabled ? "#A9A9A9" : ""
      };
};

export const StopInfoItem = ({
  index,
  stop,
  color,
  disabled,
  cardConfigurations,
  isTimeFormat24hr,
  timezoneOfJobStop
}: StopInfoItemProps) => {
  const isCompletedStop = isCompleted(stop);
  const {cardType} = cardConfigurations;

  const isFirstStopAndFirstStopEnabled = useMemo(
    () => index === 0 && cardConfigurations.topStopInformation === TopStopInformationValue.FirstStopInManifest,
    [cardConfigurations.topStopInformation, index]
  );

  const styles = getStopItemStyles({stop, color, configurationState: cardConfigurations, disabled});

  const timeValue = getTimeStampValueByType(stop, timezoneOfJobStop, isSmallCard(cardType) ? true : isTimeFormat24hr);

  return (
    <StopItemContainer className={isSmallCard(cardType) ? "small-card" : ""}>
      <StopTime
        color={color && !isCompletedStop ? IndicatorColors[color as UserColor].hex : ""}
        textColor={styles.time}
        isTimeFormat24hr={isTimeFormat24hr}
      >
        {timeValue}
        {timezoneOfJobStop && timeValue !== EmptyValueStrings.notAvailable && (
          <TimeZoneTag>{stop.timeZone?.charAt(0).toUpperCase()}</TimeZoneTag>
        )}
      </StopTime>
      <StopConfiguredInfo
        stop={stop}
        textColor={styles.address}
        cardConfigurations={cardConfigurations}
        showFirstStopLabel={isFirstStopAndFirstStopEnabled}
        isTimeFormat24hr={isTimeFormat24hr}
        timezoneOfJobStop={timezoneOfJobStop}
      />
      <StopTypeIcon>{renderStopTypeIcon(stop.stopType, styles.stopType, 16)}</StopTypeIcon>
    </StopItemContainer>
  );
};

//Styles
const HeaderContainer = styled.div<{disabled: boolean}>`
  display: grid;
  grid-template-columns: minmax(0, auto) 1fr;
  align-items: center;
  gap: 4px;
  padding: 7px 7px 4px 7px;
  width: 100%;
  height: 100%;
  border-bottom: solid#cfd4df 1px;

  & a {
    font-size: 12px;
    font-weight: 500;
    color: ${(props) => (props.disabled ? "rgba(95, 107, 124, 0.6)" : "#406AFF")};
  }
`;

const LeftSideHeader = styled.div`
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr);
  gap: 4px;
  align-items: center;
`;

const RightSideHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 7px;
`;

const Divider = styled.div<{height?: number}>`
  height: ${(props) => (props.height ? props.height + "px" : "20px")};
  width: 1px;
  border-radius: 0.5px;
  background-color: #cfd4df;
`;

const StopItemContainer = styled.div`
  height: 20px;
  font-size: 12px;
  display: grid;
  grid-template-columns: 56px auto 16px;
  gap: 0 4px;
  align-items: center;

  &.small-card {
    grid-template-columns: 40px auto 16px !important;
  }
`;

const StopConfiguredInfoContainer = styled.div<{textColor: string; showOnSiteBorder?: boolean}>`
  color: ${(props) => props.textColor};
  line-height: 20px;
  font-weight: 400;
  letter-spacing: -0.5px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;

  ${(props) =>
    props.showOnSiteBorder &&
    css`
      border-radius: 6px;
      border: 1px solid rgba(50, 164, 103, 1);
    `}
`;

const PrimaryText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SecondaryText = styled(PrimaryText)`
  color: #797979;
`;

const StopInfoHoverContainer = styled.div`
  font-family: "Roboto", sans-serif;
  font-size: 10px;
  color: #797979;
  font-weight: 400;
  .primary {
    font-size: 12px;
    color: #161616;
    margin-bottom: 5px;
  }
`;

const StopTypeIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LocationMarkerContainer = styled.div``;

const CustomTag = styled(Tag)`
  color: rgba(50, 164, 103, 1);
  font-size: 10px;
  background-color: rgba(50, 164, 103, 0.18);
  padding: 0 2px;
  letter-spacing: -0.5px;
  border-radius: 4px;
  width: fit-content;
  height: 16px;
  font-weight: 500;
`;

const DistributedGrid = styled.div<{spaceDistribution: number[]}>`
  display: grid;
  grid-template-columns: ${(props) => props.spaceDistribution.map((width) => `${width}px`).join(" ")};
  gap: 0 4px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TimeZoneTag = styled.span`
  position: absolute;
  font-size: 7px;
  top: -4px;
`;
